import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchBlogPost } from '@/lib/api';
import { CTASection } from '@/components/shared/Section';
import { ArrowLeft, Calendar, Clock, User } from 'lucide-react';

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    setPost(null);
    fetchBlogPost(slug).then(setPost).catch(() => setError('Post not found'));
  }, [slug]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-display font-bold text-3xl text-slate-900">Post not found</h1>
        <Link to="/insights" className="mt-6 inline-flex items-center gap-2 text-tito-blue font-medium">
          <ArrowLeft className="h-4 w-4" /> Back to Insights
        </Link>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32">
        <div className="h-8 w-32 bg-slate-200 rounded-sm animate-pulse" />
        <div className="mt-6 h-12 bg-slate-200 rounded-sm animate-pulse" />
        <div className="mt-3 h-12 w-3/4 bg-slate-200 rounded-sm animate-pulse" />
      </div>
    );
  }

  return (
    <>
      <article>
        <div className="bg-[#003B73] text-white">
          <div className="max-w-3xl mx-auto px-6 md:px-12 py-20 md:py-24">
            <Link to="/insights" className="inline-flex items-center gap-2 text-tito-gold text-sm font-medium hover:underline">
              <ArrowLeft className="h-4 w-4" /> All Insights
            </Link>
            <div className="mt-6 eyebrow text-tito-gold">{post.category}</div>
            <h1 className="mt-4 font-display font-bold text-4xl sm:text-5xl tracking-tight leading-[1.05]">
              {post.title}
            </h1>
            <div className="mt-8 flex flex-wrap items-center gap-5 text-sm text-blue-100/80">
              <span className="inline-flex items-center gap-1.5"><User className="h-4 w-4" />{post.author}</span>
              <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" />{post.published_at}</span>
              <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" />{post.read_time}</span>
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 md:px-12 -mt-12">
          <img src={post.cover_image} alt={post.title} className="rounded-sm w-full aspect-[16/9] object-cover border border-slate-200 shadow-xl" />
        </div>

        <div className="max-w-3xl mx-auto px-6 md:px-12 py-16">
          <div className="prose prose-slate max-w-none font-display-body" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {post.content.split('\n\n').map((para, i) => (
              <p key={i} className="text-slate-700 text-lg leading-[1.8] mb-6">{para}</p>
            ))}
          </div>
        </div>
      </article>

      <CTASection />
    </>
  );
}
